import { FunctionComponent, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  IconButton,
  Fade,
  Slide,
  CircularProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import { XCircle, X, AlertCircle, Calendar, DollarSign, Check, CreditCard } from 'react-feather'
import { Subscription } from '../../types'
import usePatronStore from '../../stores/patron'
import useSubscriptionStore from '../../stores/subscription'
import useAuthStore from '../../stores/auth'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

const SubscriptionsView: FunctionComponent = () => {
  const { id: patronId } = useParams<{ id: string }>()
  const cancelSubscription = useSubscriptionStore((state) => state.cancelSubscription)
  const getPatron = usePatronStore((state) => state.getPatron)
  const user = useAuthStore((state) => state.user)
  const isSuperAdmin = user?.role === 'super_admin'
  const patron = usePatronStore((state) => {
    if (!patronId) return null
    return state.patrons[patronId]
  })

  const [cancelDialogOpen, setCancelDialogOpen] = useState(false)
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null)
  const [sendNotification, setSendNotification] = useState(true)
  const [canceling, setCanceling] = useState(false)

  const styles = useStyles()

  const handleCancelClick = (subscription: Subscription) => {
    setSelectedSubscription(subscription)
    setCancelDialogOpen(true)
  }

  const handleCancelConfirm = async () => {
    if (!selectedSubscription || !patronId) return

    try {
      setCanceling(true)
      await cancelSubscription(selectedSubscription.id, sendNotification)
      await getPatron(patronId)
      toast.success('Subscription canceled successfully')
      setCancelDialogOpen(false)
      setSelectedSubscription(null)
      setSendNotification(true)
    } catch (error) {
      toast.error('Failed to cancel subscription')
    } finally {
      setCanceling(false)
    }
  }

  const handleCancelDialogClose = () => {
    if (!canceling) {
      setCancelDialogOpen(false)
      setSelectedSubscription(null)
      setSendNotification(true)
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return {
          color: '#10B981',
          bgColor: 'rgba(16, 185, 129, 0.1)',
          label: 'Active',
          icon: Check
        }
      case 'canceled':
      case 'cancel':
        return {
          color: '#EF4444',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          label: 'Canceled',
          icon: XCircle
        }
      case 'pending_cancel':
      case 'pending cancel':
        return {
          color: '#F59E0B',
          bgColor: 'rgba(245, 158, 11, 0.1)',
          label: 'Pending Cancel',
          icon: AlertCircle
        }
      case 'expired':
        return {
          color: '#f5590bff',
          bgColor: 'rgba(245, 158, 11, 0.1)',
          label: 'Expired',
          icon: AlertCircle
        }
      default:
        return {
          color: '#6B7280',
          bgColor: 'rgba(107, 114, 128, 0.1)',
          label: 'Unknown',
          icon: AlertCircle
        }
    }
  }

  if (!patron) {
    return (
      <Box className={styles.loadingContainer}>
        <CircularProgress size={40} />
        <Typography variant="body1" style={{ marginTop: 16, color: '#6B7280' }}>
          Loading subscriptions...
        </Typography>
      </Box>
    )
  }

  const subscriptions = (patron.subscriptions || []).sort((a, b) => {
    const statusA = a.status.toLowerCase()
    const statusB = b.status.toLowerCase()

    const activeStatuses = ['active', 'pending_cancel', 'pending_active']
    const aIsActive = activeStatuses.includes(statusA)
    const bIsActive = activeStatuses.includes(statusB)

    // Active subscriptions come first
    if (aIsActive && !bIsActive) return -1
    if (!aIsActive && bIsActive) return 1

    // Within same category, maintain original order
    return 0
  })

  if (!subscriptions.length) {
    return (
      <Box className={styles.emptyState}>
        <Box className={styles.emptyIcon}>
          <CreditCard size={48} color="#CBD5E1" />
        </Box>
        <Typography variant="h5" className={styles.emptyTitle}>
          No Subscriptions Found
        </Typography>
        <Typography variant="body1" className={styles.emptySubtitle}>
          This patron doesn't have any active subscriptions with your organization.
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <Box>
          <Typography variant="h4" className={styles.pageTitle}>
            Subscriptions
          </Typography>
          <Typography variant="body2" className={styles.pageSubtitle}>
            Manage patron membership subscriptions
          </Typography>
        </Box>
      </Box>

      <Box className={styles.subscriptionsGrid}>
        {subscriptions.map((subscription: Subscription) => {
          const statusConfig = getStatusConfig(subscription.status)
          const StatusIcon = statusConfig.icon
          const statusLower = subscription.status.toLowerCase()
          const isActive = statusLower !== 'cancel' && statusLower !== 'canceled'

          return (
            <Fade in={true} key={subscription.id} timeout={500}>
              <Box className={styles.card}>
                {/* Gradient Background Accent */}
                <Box
                  className={styles.cardGradient}
                  style={{
                    background: isActive
                      ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.05) 100%)'
                      : 'linear-gradient(135deg, rgba(107, 114, 128, 0.05) 0%, rgba(156, 163, 175, 0.02) 100%)'
                  }}
                />

                {/* Card Content */}
                <Box className={styles.cardContent}>
                  {/* Header */}
                  <Box className={styles.cardHeader}>
                    <Box className={styles.cardTitleSection}>
                      <Typography className={styles.membershipTitle}>
                        {subscription.membership?.title || subscription.membershipTitle}
                      </Typography>
                      <Box className={styles.priceTag}>
                        <DollarSign size={14} />
                        <Typography className={styles.priceText}>
                          {subscription.price}
                        </Typography>
                        <Typography className={styles.intervalText}>
                          / {subscription.interval.toLowerCase()}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Status Badge */}
                    <Box
                      className={styles.statusBadge}
                      style={{
                        backgroundColor: statusConfig.bgColor,
                        borderColor: statusConfig.color,
                      }}
                    >
                      <StatusIcon size={12} color={statusConfig.color} />
                      <Typography
                        className={styles.statusText}
                        style={{ color: statusConfig.color }}
                      >
                        {statusConfig.label}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box className={styles.divider} />

                  {/* Details */}
                  <Box className={styles.detailsSection}>
                    <Box className={styles.detailRow}>
                      <Box className={styles.detailIcon}>
                        <Calendar size={14} color="#6B7280" />
                      </Box>
                      <Box>
                        <Typography className={styles.detailLabel}>Start Date</Typography>
                        <Typography className={styles.detailValue}>
                          {subscription.startDate}
                        </Typography>
                      </Box>
                    </Box>

                    {subscription.currentCycleEnd && (
                      <Box className={styles.detailRow}>
                        <Box className={styles.detailIcon}>
                          <Calendar size={14} color="#6B7280" />
                        </Box>
                        <Box>
                          <Typography className={styles.detailLabel}>Current Cycle End</Typography>
                          <Typography className={styles.detailValue}>
                            {subscription.currentCycleEnd}
                          </Typography>
                        </Box>
                      </Box>
                    )}

                    {subscription.renewalText && (
                      <Box className={styles.renewalBanner}>
                        <AlertCircle size={14} color="#6366F1" />
                        <Typography className={styles.renewalText}>
                          {subscription.renewalText}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Action Button */}
                  {isActive && isSuperAdmin && (
                    <Box className={styles.actionSection}>
                      <Button
                        onClick={() => handleCancelClick(subscription)}
                        className={styles.cancelButton}
                        startIcon={<XCircle size={16} />}
                        fullWidth
                      >
                        Cancel Subscription
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>
            </Fade>
          )
        })}
      </Box>

      {/* Modern Cancel Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Slide}
        TransitionProps={{ direction: 'up' } as any}
        PaperProps={{
          className: styles.dialog
        }}
      >
        <Box className={styles.dialogHeader}>
          <Box className={styles.dialogIconContainer}>
            <AlertCircle size={24} color="#EF4444" />
          </Box>
          <IconButton
            onClick={handleCancelDialogClose}
            className={styles.dialogClose}
            disabled={canceling}
          >
            <X size={20} />
          </IconButton>
        </Box>

        <DialogTitle className={styles.dialogTitle}>
          Cancel Subscription?
        </DialogTitle>

        <DialogContent className={styles.dialogContent}>
          <Typography className={styles.dialogText}>
            You're about to cancel the subscription for{' '}
            <strong className={styles.dialogHighlight}>
              {selectedSubscription?.membership?.title || selectedSubscription?.membershipTitle}
            </strong>
            . This action cannot be undone.
          </Typography>

          <Box className={styles.notificationSection}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendNotification}
                  onChange={(e) => setSendNotification(e.target.checked)}
                  color="primary"
                  disabled={canceling}
                />
              }
              label={
                <Typography className={styles.checkboxLabel}>
                  Send cancellation notification email to patron
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions className={styles.dialogActions}>
          <Button
            onClick={handleCancelDialogClose}
            className={styles.dialogCancelButton}
            disabled={canceling}
          >
            Go Back
          </Button>
          <Button
            onClick={handleCancelConfirm}
            className={styles.dialogConfirmButton}
            disabled={canceling}
            startIcon={canceling ? <CircularProgress size={16} color="inherit" /> : <XCircle size={16} />}
          >
            {canceling ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    padding: '32px',
    maxWidth: '1400px',
    margin: '0 auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
  header: {
    marginBottom: '32px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 700,
    color: '#111827',
    marginBottom: '4px',
  },
  pageSubtitle: {
    fontSize: '14px',
    color: '#6B7280',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '48px',
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    backgroundColor: '#F3F4F6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  emptyTitle: {
    fontSize: '20px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  emptySubtitle: {
    fontSize: '14px',
    color: '#6B7280',
    textAlign: 'center',
    maxWidth: '400px',
  },
  subscriptionsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
    gap: '24px',
  },
  card: {
    position: 'relative',
    borderRadius: '16px',
    overflow: 'hidden',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E7EB',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      borderColor: '#D1D5DB',
    },
  },
  cardGradient: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
  },
  cardContent: {
    padding: '24px',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '16px',
  },
  cardTitleSection: {
    flex: 1,
  },
  membershipTitle: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#111827',
    marginBottom: '8px',
    lineHeight: 1.3,
  },
  priceTag: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  priceText: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#6366F1',
  },
  intervalText: {
    fontSize: '14px',
    color: '#6B7280',
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '12px',
    border: '1px solid',
    flexShrink: 0,
  },
  statusText: {
    fontSize: '12px',
    fontWeight: 600,
    letterSpacing: '0.025em',
  },
  divider: {
    height: '1px',
    backgroundColor: '#F3F4F6',
    margin: '20px 0',
  },
  detailsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  detailIcon: {
    width: '28px',
    height: '28px',
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  detailLabel: {
    fontSize: '12px',
    color: '#6B7280',
    marginBottom: '2px',
  },
  detailValue: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  renewalBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: 'rgba(99, 102, 241, 0.05)',
    borderRadius: '8px',
    marginTop: '4px',
  },
  renewalText: {
    fontSize: '13px',
    color: '#4F46E5',
    fontStyle: 'italic',
  },
  actionSection: {
    marginTop: '20px',
    paddingTop: '20px',
    borderTop: '1px solid #F3F4F6',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#DC2626',
    border: '1px solid #FCA5A5',
    borderRadius: '10px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    transition: 'all 0.2s',
    '&:hover': {
      backgroundColor: 'rgba(220, 38, 38, 0.05)',
      borderColor: '#DC2626',
    },
  },
  dialog: {
    borderRadius: '20px !important',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25) !important',
  },
  dialogHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 24px 0',
  },
  dialogIconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  dialogClose: {
    color: '#6B7280',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  dialogTitle: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#111827',
    padding: '16px 24px 8px',
  },
  dialogContent: {
    padding: '0 24px 24px !important',
  },
  dialogText: {
    fontSize: '15px',
    color: '#4B5563',
    lineHeight: 1.6,
  },
  dialogHighlight: {
    color: '#111827',
    fontWeight: 600,
  },
  notificationSection: {
    marginTop: '24px',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    borderRadius: '12px',
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#374151',
  },
  dialogActions: {
    padding: '16px 24px 24px !important',
    gap: '12px',
  },
  dialogCancelButton: {
    color: '#6B7280',
    borderRadius: '10px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F3F4F6',
    },
  },
  dialogConfirmButton: {
    backgroundColor: '#DC2626',
    color: '#FFFFFF',
    borderRadius: '10px',
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#B91C1C',
    },
    '&:disabled': {
      backgroundColor: '#FCA5A5',
      color: '#FFFFFF',
    },
  },
})

export default SubscriptionsView
